import React, { useCallback, useMemo, useState } from 'react';
import { injectIntl, IntlShape } from 'react-intl';
import { get, isEmpty, isNull } from 'lodash';
import { Listings } from 'containers/AppContainer/typings';
import {
  CorporealDocument,
  JudgementADDDocumentType,
} from 'containers/common/LegalProceedings/JudgementAdd/Forms/JudgementAddPrononcings/typings';
import { LISTINGS, PROCEDURE_STEPS } from 'containers/data-structure';
import JudgementAddComplement from 'containers/common/LegalProceedings/JudgementAdd/Recap/Pronouncing/JudgementAddComplement';
import ExpertisesRecap from 'containers/common/LegalProceedings/JudgementAdd/Recap/Pronouncing/ExpertisesRecap';
import DocumentRecap from 'containers/common/LegalProceedings/JudgementAdd/Recap/Pronouncing/DocumentRecap';
import DropDownDecisionMenu from 'components/SahamDropDown/DropDownDecisionMenu';
import messages from 'containers/common/LegalProceedings/JudgementAdd/Recap/Pronouncing/messages';
import { translate } from 'containers/services/translate';
import { IOpenState } from 'containers/CourtManagerLegalProceedings/JudgementAdd/typings';
import {
  DIALOGS,
  DIALOGS_STATE,
} from 'containers/CourtManagerLegalProceedings/JudgementAdd/constants';
import {
  activateDecisionButton,
  formatFeeNoteToGenerate,
  validateAfterRejection,
} from 'containers/common/LegalProceedings/JudgementAdd/Recap/Pronouncing/utils';
import { Grid } from '@material-ui/core';
import { Container } from 'containers/common/LegalProceedings/JudgementAdd/Recap/Pronouncing/styled-component';
import PronouncingValidationDialog from 'containers/CourtManagerLegalProceedings/JudgementAdd/Dialogs/PronouncingValidationDialog';
import {
  IJudgementAdd,
  IUploadedDocument,
  JudgementOnMeritsType,
} from 'containers/LawyerLegalProceedingsPage/typings';
import PronouncingRejectionDialog from 'containers/CourtManagerLegalProceedings/JudgementAdd/Dialogs/PronouncingRejectionDialog';
import JudgementAddDecisionRecap from 'containers/common/LegalProceedings/JudgementAdd/Recap/JudgementAddDecisionRecap';
import JudgementAddDocumentDecisionRecap from 'containers/common/LegalProceedings/JudgementAdd/Recap/JudgementAddDocumentDecisionRecap';
import { toBeEdited } from 'containers/common/LegalProceedings/JudgementAdd/Recap/utils';
import PrimaryButton from 'components/Buttons/PrimaryButton';
import { BUTTON_COLORS } from 'constants/design';
import currentIntervenantStorage from 'containers/services/security/current-intervenant-storage';
import FirmReferenceForm from 'components/FirmReferenceForm';
import * as featureService from 'features/fflip-helpers';
import { FEATURES } from 'features/features';
import { ExpertReportType } from 'containers/common/LegalProceedings/ExpertReport/typings';
import {
  STATUS_PENDING,
  STATUS_VALIDATED,
} from 'containers/LawyerFeeNote/constants';
import UpdateStepRecap from 'containers/common/LegalProceedings/JudgementAdd/Recap/UpdateStepRecap';
import userPrivileges, {
  PRIVILEGES,
} from 'containers/services/security/user-privileges';
import { isBlockStatusClaim } from 'containers/CourtManagerLegalProceedings/utils';
import { useSelector } from 'react-redux';
import { makeSelectFeeNotes } from 'containers/LawyerFeeNote/selectors';
import { getPendingFeeNotesByStep } from 'containers/LawyerLegalProceedingsPage/judgementAdd/utils';

export interface JudgementAddPronouncingRecapProps {
  intl: IntlShape;
  listings: Listings;
  judgementAddDocument: JudgementADDDocumentType;
  judgementAdd?: IJudgementAdd | {};
  requestMakeJudgementAddDocumentDecision?: Function;
  isManager: boolean;
  resetUploadedDocument: Function;
  uploadDocument: Function;
  uploadedDocument: IUploadedDocument;
  document?: CorporealDocument;
  setDocument?: Function;
  docType?: string;
  docId?: string;
  setDocumentId?: Function;
  documentId?: string;
  claimNumber?: string;
  setJudgementAdd?: Function;
  requestUpdateJudgementAddDocument?: Function;
  judiciaryProcessId?: number;
  setDialogOpenFirmReference?: Function;
  requestGetJudgementAdd?: Function;
  expertReport?: ExpertReportType;
  judgementOnMerits?: JudgementOnMeritsType;
  handleCivilPartyDialog?: Function;
  sagisVictimInfo: any;
  sagisOpposingParties: any;
  claimAssignment: any;
  feeNotes: any | [];
  setOpenFeeNoteDialog: Function;
  isBasicManagement?: boolean;
  policyInfo: any;
  requestGetDocByDocumentumId: Function;
  setOpenInvoiceFeeNoteUpdateInfoDialog: Function;
  openInvoiceFeeNoteUpdateInfoDialog: boolean;
  setJudgemenAddDocumentToUpdate: Function;
  handleValidateUpdateJudgementAddDocument: Function;
}

const JudgementAddPronouncingRecap: React.FC<JudgementAddPronouncingRecapProps> = (
  props,
): JSX.Element => {
  const [isDialogOpen, setIsDialogOpen] = useState<IOpenState>({
    ...DIALOGS_STATE,
  });
  const feeNotesStore = useSelector(makeSelectFeeNotes());
  const [dropDown, setDropDown] = useState<null | HTMLElement>(null);
  const handleIsDialogOpen = (
    dialog: string,
    value?: boolean,
    toUpdate: boolean = false,
  ) => () => {
    setIsDialogOpen({ ...isDialogOpen, [dialog]: !!value, toUpdate });
  };

  const expertises = useMemo(() => {
    return get(props.judgementAddDocument, 'expertises', []).filter(
      (el: any) => !isEmpty(el.victims) || !isEmpty(el.vehicules),
    );
  }, [props.judgementAddDocument]);

  const dropDownMenuElements = useCallback(() => {
    const pronouncingDecision = [
      {
        title: translate(messages.courtManagerJudgementValidate, props),
        action: handleIsDialogOpen(DIALOGS.VALIDATION, true),
      },
      {
        title: translate(messages.courtManagerJudgementRefuse, props),
        action: handleIsDialogOpen(DIALOGS.REFUSAL, true),
      },
    ];
    const updatePronouncing = {
      title: translate(messages.courtManagerJudgementUpdate, props),
      action: handleIsDialogOpen(DIALOGS.VALIDATION, true, true),
    };
    return [
      get(props.judgementAddDocument, 'status') === STATUS_PENDING ||
      validateAfterRejection(props.judgementAddDocument)
        ? [
            ...pronouncingDecision,
            featureService.hasFeature(FEATURES.MANAGE_CLAIM_BY_MANAGER) &&
            get(props.judgementAddDocument, 'status') !== STATUS_PENDING &&
            get(props.judgementAdd, 'status') !== STATUS_PENDING
              ? updatePronouncing
              : null,
          ]
        : featureService.hasFeature(FEATURES.MANAGE_CLAIM_BY_MANAGER) &&
          get(props.judgementAddDocument, 'status') !== STATUS_PENDING &&
          get(props.judgementAdd, 'status') !== STATUS_PENDING
        ? updatePronouncing
        : null,
    ]
      .flat()
      .filter(item => !isNull(item)) as any;
  }, [get(props.judgementAddDocument, 'status')]);
  const feeNotes = useMemo(
    () =>
      getPendingFeeNotesByStep(
        feeNotesStore,
        PROCEDURE_STEPS.JUDGEMENT_ADD,
        get(props.judgementAdd, 'id'),
        get(props.judgementAddDocument, 'id')
          ? [get(props.judgementAddDocument, 'id')]
          : [],
        currentIntervenantStorage?.get()?.sagis_code,
      ),
    [
      props.feeNotes,
      get(props.judgementAddDocument, 'createdAt', ''),
      get(props.judgementAddDocument, 'id'),
      get(props.judgementAdd, 'id'),
    ],
  );
  const handleValidateUpdate = () => {
    if (
      featureService.hasFeature(FEATURES.LIMIT_INVOICE_DATE) &&
      !isEmpty(feeNotes) &&
      !props.isManager
    ) {
      props.setJudgemenAddDocumentToUpdate(props.judgementAddDocument);
      props.setOpenInvoiceFeeNoteUpdateInfoDialog(true);
    } else {
      props.setJudgemenAddDocumentToUpdate(props.judgementAddDocument);
      return props.handleValidateUpdateJudgementAddDocument(
        props.judgementAddDocument,
      );
    }
  };

  const isCreator = useMemo(
    () =>
      props.isManager
        ? true
        : isNull(get(props.judgementAddDocument, 'uploadedBy'))
        ? get(props.judgementAdd, 'creator', '')
        : get(props.judgementAddDocument, 'uploadedBy') ==
          currentIntervenantStorage.get()?.preferred_username,
    [
      props.isManager,
      get(props.judgementAddDocument, 'uploadedBy'),
      get(props.judgementAdd, 'creator', ''),
    ],
  );

  const lawyerAssignment = useMemo(
    () =>
      props.claimAssignment?.find(
        (el: any) =>
          el.assignedLawyer === currentIntervenantStorage.get()?.sagis_code &&
          el.judiciaryProcessId === props.judiciaryProcessId &&
          el.isDeleted === false,
      ),
    [props.judiciaryProcessId, props.claimAssignment],
  );
  const feeNoteAssignment = useMemo(
    () =>
      get(props, 'feeNotes', [])
        .sort(
          (a: any, b: any) =>
            new Date(get(a, 'createdAt')).getTime() -
            new Date(get(b, 'createdAt')).getTime(),
        )
        .find((el: any) =>
          el.judgementAddDocumentId
            ? el.judgementAddDocumentId ===
                get(props.judgementAddDocument, 'id') &&
              el.codeIntervenant === currentIntervenantStorage.get()?.sagis_code
            : el.step === PROCEDURE_STEPS.JUDGEMENT_ADD &&
              new Date(el.createdAt).getTime() >=
                new Date(
                  get(props.judgementAddDocument, 'createdAt', ''),
                ).getTime(),
        ),
    [
      props.feeNotes,
      get(props.judgementAddDocument, 'createdAt', ''),
      get(props.judgementAddDocument, 'id'),
    ],
  );
  const lawyerFirmReference = useMemo(
    () => get(lawyerAssignment, 'firmReference', null),
    [props.judiciaryProcessId, props.claimAssignment],
  );

  const lawyerAssignmentDate = get(
    lawyerAssignment,
    'lawyerAssignmentDate',
    get(lawyerAssignment, 'createdAt', null),
  );

  return (
    <Container>
      {!props.isManager &&
        lawyerAssignment &&
        !isBlockStatusClaim(props.policyInfo) &&
        get(props.judgementAddDocument, 'id') && (
          <FirmReferenceForm
            firmReference={
              feeNoteAssignment
                ? get(feeNoteAssignment, 'firmReference')
                : lawyerFirmReference
            }
            formInputLabel={translate(
              messages.lawyerFirmReferenceDialogLabel,
              props,
            )}
            formInputName={translate(
              messages.lawyerFirmReferenceDialogLabel,
              props,
            )}
            validationButtonTitle={translate(messages.generateFeeNote, props)}
            setIsDialogOpenFirmReference={props.setDialogOpenFirmReference}
            isFeeNoteGenerated={feeNoteAssignment}
            isPronoucing
            feeNote={formatFeeNoteToGenerate(
              props.judgementAddDocument,
              lawyerFirmReference,
              props.claimNumber,
              props.judgementAdd,
            )}
            judiciaryProcessId={props.judiciaryProcessId}
            isAssignmentDateValid={
              new Date(lawyerAssignmentDate).getTime() <=
              new Date(
                get(props.judgementAddDocument, 'createdAt', ''),
              ).getTime()
            }
            assignment={lawyerAssignment}
            lawyerFirmReference={lawyerFirmReference}
          />
        )}
      <JudgementAddComplement
        judgementAddDocuments={props.judgementAddDocument}
        listings={props.listings}
        isManager={props.isManager}
      />
      <ExpertisesRecap
        expertises={expertises}
        expertiseList={get(props.listings, LISTINGS.EXPERTISE, [])}
        sagisVictimInfo={props.sagisVictimInfo}
        sagisOpposingParties={props.sagisOpposingParties}
        isBasicManagement={props.isBasicManagement}
      />
      <Grid container direction="row">
        <Grid item xs={6}>
          <DocumentRecap
            judgementAddDocuments={props.judgementAddDocument}
            requestGetDocByDocumentumId={props.requestGetDocByDocumentumId}
          />
        </Grid>
        {activateDecisionButton(props.judgementAddDocument, props.isManager) &&
        get(props.judgementAddDocument, 'status') !== STATUS_VALIDATED ? (
          <Grid item xs={6} className="judgement-decision">
            <DropDownDecisionMenu
              setDropDown={setDropDown}
              dropDown={dropDown}
              label={translate(messages.managerDecision, props)}
              menuElements={dropDownMenuElements()}
            />
          </Grid>
        ) : (
          featureService.hasFeature(FEATURES.MANAGE_CLAIM_BY_MANAGER) &&
          props.isManager &&
          !userPrivileges.userHasPrivilege(
            PRIVILEGES.ISCOURTMONITORINGMANAGER,
          ) &&
          get(props.judgementAddDocument, 'status') !== STATUS_PENDING &&
          get(props.judgementAdd, 'status') !== STATUS_PENDING && (
            <Grid item xs={6} className="judgement-update">
              <PrimaryButton
                title={translate(messages.courtManagerJudgementUpdate, props)}
                onClick={
                  get(props.judgementAddDocument, 'status') === STATUS_VALIDATED
                    ? handleIsDialogOpen(DIALOGS.VALIDATION, true, true)
                    : props.handleCivilPartyDialog &&
                      props.handleCivilPartyDialog(
                        DIALOGS.VALIDATION,
                        true,
                        true,
                        false,
                      )
                }
                background={BUTTON_COLORS.PRIMARY_BLUE}
                border={BUTTON_COLORS.PRIMARY_BLUE}
                titlecolor={BUTTON_COLORS.BLUE_BUTTON_TITLE}
              />
            </Grid>
          )
        )}
        <Grid container direction="column">
          <Grid item xs={6}>
            <JudgementAddDocumentDecisionRecap
              judgementAddDocument={props.judgementAddDocument}
            />
            <JudgementAddDecisionRecap
              judgementAdd={props.judgementAdd}
              judgementAddDocument={props.judgementAddDocument}
            />
            <UpdateStepRecap
              updatedBy={get(props, 'judgementAddDocument.updatedBy', '')}
            />
          </Grid>
          {toBeEdited(props.judgementAddDocument, props.isManager) &&
            isCreator &&
            !userPrivileges.userHasPrivilege(
              PRIVILEGES.ISCOURTMONITORINGMANAGER,
            ) && (
              <Grid item xs={6} className="button">
                <PrimaryButton
                  className="button"
                  title={translate(messages.validate, props)}
                  data-testId="validate"
                  onClick={handleValidateUpdate}
                  background={BUTTON_COLORS.PRIMARY_BLUE}
                  border={BUTTON_COLORS.PRIMARY_BLUE}
                  titlecolor={BUTTON_COLORS.BLUE_BUTTON_TITLE}
                />
              </Grid>
            )}
        </Grid>
      </Grid>
      <PronouncingValidationDialog
        judgementAddDocument={props.judgementAddDocument}
        open={isDialogOpen[DIALOGS.VALIDATION]}
        handleClose={handleIsDialogOpen(DIALOGS.VALIDATION)}
        lawyer={get(props.judgementAdd, 'creator', '')}
        claimId={get(props.judgementAdd, 'claimId', '')}
        judgementAdd={props.judgementAdd}
        requestMakeJudgementAddDocumentDecision={
          props.requestMakeJudgementAddDocumentDecision
        }
        setJudgementAdd={props.setJudgementAdd}
        claimNumber={props.claimNumber}
        requestGetJudgementAdd={props.requestGetJudgementAdd}
        expertReport={props.expertReport}
        judgementOnMerits={props.judgementOnMerits}
        toUpdate={get(isDialogOpen, 'toUpdate', false)}
        setOpenFeeNoteDialog={props.setOpenFeeNoteDialog}
      />
      <PronouncingRejectionDialog
        isRefuseDialogOpen={isDialogOpen[DIALOGS.REFUSAL]}
        handleCancel={handleIsDialogOpen(DIALOGS.REFUSAL)}
        requestMakeJudgementAddDocumentDecision={
          props.requestMakeJudgementAddDocumentDecision
        }
        claimId={get(props.judgementAdd, 'claimId', '')}
        lawyer={get(props.judgementAdd, 'creator', '')}
        judgementAdd={props.judgementAdd}
        judgementAddDocument={props.judgementAddDocument}
        claimNumber={props.claimNumber}
        setJudgementAdd={props.setJudgementAdd}
        setOpenFeeNoteDialog={props.setOpenFeeNoteDialog}
      />
    </Container>
  );
};

export default injectIntl(JudgementAddPronouncingRecap);
